// DQRuleStudio.tsx
// =================
// Fresh replacement for DQRuleTuningAssistant's chat-based flow.
// Two modes in one modal:
//   • Add Rule   — pick a category (val/cons/std) + optional English instruction,
//                  generate fresh rules for a field, preview, accept.
//   • Tune Rule  — open an existing rule, type feedback, get a refined version
//                  (same id), preview, accept.
//
// Talks to the NEW synchronous endpoint via adriva_dq_generateRule (dqAPI.ts).
// Does NOT use adriva_sendChatMsg. Persistence happens only on Accept, via the
// caller's existing DQ_RULE_ACCEPT handler (passed in as onAcceptRule).
//
// Visual language matches the DMTreeGridSummary "dq-root" system (same CSS vars,
// lc-badge classes, react-bootstrap Modal) so it reads as one product.

import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Spinner, Badge, Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles, faPlus, faPenToSquare, faTriangleExclamation,
  faCircleCheck, faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { T } from "../theme/theme";
import { DQ_RULE_CAT_LABELS } from "../constants";
import {
  adriva_dq_generateRule,
  type Category,
  type GeneratedRule,
  type RuleValidationResult,
} from "../dqAPI";

type StudioMode = "add" | "tune";

export interface DQRuleStudioProps {
  show:        boolean;
  onHide:      () => void;
  entityId:    string;
  appNm:       string;
  // The field the rule is for (from field.profile)
  sourcePrefix: string;
  column:       string;          // orig_column_name
  columnLabel?: string;          // display name
  // "add" => generate new; "tune" => refine existingRule
  mode:         StudioMode;
  existingRule?: any | null;     // required when mode === "tune"
  // Persist via the caller's existing DQ_RULE_ACCEPT flow
  onAcceptRule: (rule: GeneratedRule, validationResult?: RuleValidationResult) => void;
}

const CAT_OPTIONS: { key: Category; label: string; hint: string }[] = [
  { key: "val",  label: "Validation",  hint: "Catch invalid values (reject / warn)" },
  { key: "cons", label: "Consistency", hint: "Cross-field / cross-table checks" },
  { key: "std",  label: "Standardization", hint: "Clean & normalize values (auto-fix)" },
];

const STATUS_STYLE: Record<string, { bg: string; fg: string; border: string; icon: any }> = {
  PASS:    { bg: T.greenBg, fg: T.green, border: T.greenBorder, icon: faCircleCheck },
  APPLIED: { bg: T.greenBg, fg: T.green, border: T.greenBorder, icon: faCircleCheck },
  FAIL:    { bg: T.redBg,   fg: T.red,   border: T.redBorder,   icon: faCircleXmark },
  ERROR:   { bg: T.amberBg, fg: T.amber, border: T.amberBorder, icon: faTriangleExclamation },
};

function StatusChip({ status }: { status?: string }) {
  if (!status) return null;
  const s = STATUS_STYLE[status] || { bg: T.bgSidebar, fg: T.textMuted, border: T.border, icon: faTriangleExclamation };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
    }}>
      <FontAwesomeIcon icon={s.icon} style={{ fontSize: 10 }} /> {status}
    </span>
  );
}

const DQRuleStudio: React.FC<DQRuleStudioProps> = ({
  show, onHide, entityId, appNm, sourcePrefix, column, columnLabel,
  mode, existingRule, onAcceptRule,
}) => {
  const isTune = mode === "tune";

  const [category, setCategory]   = useState<Category | "">("");
  const [directive, setDirective] = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [rules, setRules]         = useState<GeneratedRule[]>([]);
  const [results, setResults]     = useState<RuleValidationResult[]>([]);
  const [accepted, setAccepted]   = useState<Set<string>>(new Set());

  // Reset everything when the modal (re)opens or the target changes.
  useEffect(() => {
    if (show) {
      setCategory(isTune ? ((existingRule?.cat as Category) || "") : "");
      setDirective("");
      setRules([]); setResults([]); setError(null);
      setAccepted(new Set());
    }
  }, [show, isTune, existingRule, sourcePrefix, column]);

  const resultByRuleId = useMemo(() => {
    const m: Record<string, RuleValidationResult> = {};
    results.forEach((r) => { if (r.rule_id) m[r.rule_id] = r; });
    return m;
  }, [results]);

  const canSubmit =
    !loading &&
    (isTune ? directive.trim().length > 0
            : (category !== "" || directive.trim().length > 0));

  const handleGenerate = async () => {
    setLoading(true); setError(null); setRules([]); setResults([]);
    try {
      const res = await adriva_dq_generateRule({
        entity_id:     entityId,
        app_nm:        appNm,
        source_prefix: sourcePrefix,
        column,
        category:      isTune ? ((existingRule?.cat as Category) ?? null) : (category || null),
        user_directive: directive.trim() || null,
        existing_rule:  isTune ? existingRule : null,
        execute_preview: true,
      });
      if (!res.success) {
        setError(res.error || "Rule generation failed.");
      } else if (!res.rules?.length) {
        setError("The assistant did not return any rule for this request. Try rephrasing, or pick a different category.");
      } else {
        setRules(res.rules);
        setResults(res.validation_results || []);
      }
    } catch (e: any) {
      setError(e?.detail || e?.message || "Request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = (rule: GeneratedRule) => {
    onAcceptRule(rule, resultByRuleId[rule.id]);   // <-- send the preview result too
    setAccepted((prev) => new Set(prev).add(rule.id));
  };

  const fieldTitle = columnLabel ? `${columnLabel} (${column})` : column;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.border}` }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 800, color: T.brand, display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={isTune ? faPenToSquare : faPlus} style={{ color: T.purple }} />
          {isTune ? "Tune Rule" : "Add Rule"}
          <span style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, background: T.bgSidebar, borderRadius: 8, padding: "2px 10px" }}>
            {sourcePrefix} · {fieldTitle}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: T.bg }}>
        {/* ── Request panel ─────────────────────────────────────────────── */}
        <div className="dq-card" style={{ padding: 16, marginBottom: 16 }}>
          {isTune ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: T.textMuted, marginBottom: 6 }}>
                Refining rule
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.brand }}>{existingRule?.id}</span>
                <span className={`lc-badge lc-${existingRule?.lifecycle || "Draft"}`}>{existingRule?.lifecycle || "Draft"}</span>
                <span className="cat-chip">{DQ_RULE_CAT_LABELS[existingRule?.cat] || existingRule?.cat}</span>
              </div>
              <div style={{ fontSize: 12, color: T.textSecond, marginTop: 6 }}>{existingRule?.d}</div>
            </div>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 12, fontWeight: 700, color: T.textSecond }}>
                Rule type <span style={{ color: T.textMuted, fontWeight: 500 }}>(optional — leave blank to let the assistant choose)</span>
              </Form.Label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {CAT_OPTIONS.map((c) => {
                  const active = category === c.key;
                  return (
                    <button key={c.key} type="button" onClick={() => setCategory(active ? "" : c.key)}
                      title={c.hint}
                      className={`dq-pill ${active ? "active" : "inactive"}`}
                      style={active ? { background: T.purple, borderColor: T.purple } : {}}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: T.textSecond }}>
              {isTune ? "What should change?" : "Describe the rule you want"}
              {isTune && <span style={{ color: T.red }}> *</span>}
            </Form.Label>
            <Form.Control as="textarea" rows={3} value={directive}
              onChange={(e) => setDirective(e.target.value)}
              placeholder={isTune
                ? "e.g. Relax this to allow values up to 30, and ignore blanks."
                : "e.g. Plant code must be 2 letters followed by 2 digits. Reject anything else."}
              style={{ fontSize: 13 }} />
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
              {isTune
                ? "Your feedback is sent to the assistant; the refined rule keeps the same ID."
                : "Leave blank to use the standard generator, or describe exactly what you need and the assistant will prioritise it."}
            </div>
          </Form.Group>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <Button onClick={handleGenerate} disabled={!canSubmit}
              style={{ background: T.brand, border: "none", fontWeight: 700, fontSize: 13 }}>
              {loading
                ? <><Spinner animation="border" size="sm" /> Generating…</>
                : <><FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6 }} />
                    {isTune ? "Generate refined rule" : "Generate rules"}</>}
            </Button>
          </div>
        </div>

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {error && (
          <div style={{ fontSize: 13, color: T.red, background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────────── */}
        {rules.length > 0 && (
          <Accordion alwaysOpen defaultActiveKey={["0"]}>
            {rules.map((rule, idx) => {
              const vr = resultByRuleId[rule.id];
              const isAccepted = accepted.has(rule.id);
              return (
                <Accordion.Item eventKey={String(idx)} key={rule.id || idx}>
                  <Accordion.Header>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.brand }}>{rule.id}</span>
                      <span className="cat-chip">{DQ_RULE_CAT_LABELS[rule.cat] || rule.cat}</span>
                      {rule.sev && (
                        <Badge bg={rule.sev === "H" ? "danger" : rule.sev === "M" ? "warning" : "secondary"}>
                          {rule.sev === "H" ? "High" : rule.sev === "M" ? "Medium" : "Low"}
                        </Badge>
                      )}
                      {rule.act && <span style={{ fontSize: 11, color: T.textMuted }}>{rule.act}</span>}
                      {vr && <StatusChip status={String(vr.status)} />}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div style={{ fontSize: 13, color: T.textPrimary, marginBottom: 8 }}>{rule.d}</div>

                    {rule.bi && (
                      <div style={{ fontSize: 12, color: T.textSecond, marginBottom: 8 }}>
                        <strong>Business impact:</strong> {rule.bi}
                      </div>
                    )}
                    {rule.fix && (
                      <div style={{ fontSize: 12, color: T.textSecond, marginBottom: 8 }}>
                        <strong>Suggested fix:</strong> {rule.fix}
                      </div>
                    )}

                    {/* Technical detail */}
                    <details style={{ marginBottom: 8 }}>
                      <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: T.brand }}>Technical detail</summary>
                      <div style={{ marginTop: 6 }}>
                        {rule.vl && <div style={{ fontSize: 12 }}><strong>Logic:</strong> {rule.vl}</div>}
                        {rule.ve && <pre style={{ fontSize: 11, background: T.bgSidebar, padding: 8, borderRadius: 6, marginTop: 6 }}>{JSON.stringify(rule.ve, null, 2)}</pre>}
                        {rule.te && <pre style={{ fontSize: 11, background: T.bgSidebar, padding: 8, borderRadius: 6, marginTop: 6 }}>{JSON.stringify(rule.te, null, 2)}</pre>}
                        {Array.isArray(rule.ex) && rule.ex.length > 0 && (
                          <ul style={{ fontSize: 12, marginTop: 6 }}>
                            {rule.ex.map((e, j) => (
                              <li key={j}><span style={{ color: T.red }}>{String(e.before)}</span> → <span style={{ color: T.green }}>{String(e.after)}</span>{e.note ? ` (${e.note})` : ""}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </details>

                    {/* Execution preview */}
                    {vr && (
                      <div style={{ fontSize: 12, color: T.textSecond, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                        {vr.dimension === "Conformity"
                          ? <span><strong>Preview:</strong> {(vr.examples as any)?.affected_count ?? 0} would be auto-fixed of {vr.total_records ?? 0}</span>
                          : <span><strong>Preview:</strong> {vr.fail_count ?? 0} fail · {vr.passed_count ?? 0} pass · {vr.total_records ?? 0} total</span>}
                        {Array.isArray(vr.sample_failures) && vr.sample_failures.length > 0 && (
                          <pre style={{ fontSize: 11, background: T.bgSidebar, padding: 8, borderRadius: 6, marginTop: 6, marginBottom: 0 }}>
                            {JSON.stringify(vr.sample_failures.slice(0, 5), null, 2)}
                          </pre>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button size="sm" disabled={isAccepted}
                        onClick={() => handleAccept(rule)}
                        style={{ background: isAccepted ? T.greenBg : T.green, color: isAccepted ? T.green : "#fff", border: "none", fontWeight: 700, fontSize: 12 }}>
                        {isAccepted
                          ? <><FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: 6 }} /> Accepted</>
                          : (isTune ? "Accept refined rule" : "Accept rule")}
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: `1px solid ${T.border}` }}>
        <Button variant="light" onClick={onHide} style={{ fontSize: 13 }}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DQRuleStudio;